import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { getProjects, getPlotsByProject, createFloor, createFlat } from '../../../services/project'
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Stack,
  DialogContentText,
  Menu,
  Alert,
  CircularProgress,
  Backdrop,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  FileUpload as FileUploadIcon,
  FileDownload as FileDownloadIcon,
  Apartment as ApartmentIcon,
} from "@mui/icons-material";
import {
  readExcelFile,
  exportAsExcel,
  exportAsCSV,
  exportAsPDF,
  downloadExcelFile,
} from "../../../utils/importExport";
import { hasReadPermission, hasWritePermission } from '../../../utils/menuFilter';
import WarningIcon from '@mui/icons-material/Warning';
import AvatarMui from '@mui/material/Avatar';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const DEFAULT_AMENITIES = [
  "Clubhouse",
  "Swimming Pool",
  "Gym",
  "Sky Lounge",
  "Indoor Games",
  "Kids Play Area",
  "Rooftop Garden",
  "EV Charging",
  "Yoga Deck",
  "Community Hall",
  "Business Center",
  "Food Court",
];

const BuildingList = () => {

  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const canRead = hasReadPermission('project.apartment-commercial') || hasReadPermission('project');
  const canWrite = hasWritePermission('project.apartment-commercial') || hasWritePermission('project');

  const [buildingList, setBuildingList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedProjectName, setSelectedProjectName] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');

  const [loadingBuildings, setLoadingBuildings] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);

  const [draftType, setDraftType] = useState("");
  const [draftProject, setDraftProject] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState("");

  const [activeFilters, setActiveFilters] = useState({
    search: "",
    status: "all",
    projectType: "",
    project: "",
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const [newBuilding, setNewBuilding] = useState({
    name: "",
    project: "",
    projectId: "",
    projectType: "",
    status: "available",
    totalFloors: "",
    totalFlats: "",
    startDate: "",
    completionDate: "",
    amenities: [],
    remark: "",
  });

  const [addError, setAddError] = useState("");
  const [editBuilding, setEditBuilding] = useState({
    id: "",
    name: "",
    project: "",
    projectId: "",
    projectType: "Apartment",
    status: "null",
    totalFloors: "",
    totalFlats: "",
    floorNo: "",
    startDate: "",
    completionDate: "",
    amenities: [],
    remark: "",
  });

  const [editError, setEditError] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [editProjectCustom, setEditProjectCustom] = useState(false);
  const fileInputRef = useRef(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFileName, setImportFileName] = useState("");
  const [selectedImportFile, setSelectedImportFile] = useState(null);
  const [importError, setImportError] = useState("");
  const [importSuccess, setImportSuccess] = useState("");
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState(null);
  const [amenitiesMaster, setAmenitiesMaster] = useState([]);
  const [addSaving, setAddSaving] = useState(false);
  const lastProjectTypeLoaded = useRef(null);

  useEffect(() => {
    const fetchAmenities = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/api/globalMasterDetails?type=amenities`,
        );
        if (!response.ok) return;
        const result = await response.json();
        const list = Array.isArray(result?.data)
          ? result.data
          : Array.isArray(result?.details)
            ? result.details
            : Array.isArray(result)
              ? result
              : [];

        const normalized = list
          .map((item) => {
            if (!item) return null;
            const id = item._id || item.id;
            const label =
              item?.details?.amenitiesName ||
              item?.details?.name ||
              item?.amenitiesName ||
              item?.name ||
              item?.value ||
              item?.label;
            if (id && label) return { id, label };
            return null;
          })
          .filter((v) => v && v.id && v.label);

        // fallback to existing building amenities if API returns empty
        if (normalized.length === 0) {
          const fallback = buildingList
            .flatMap((b) =>
              Array.isArray(b.amenities)
                ? b.amenities.map((label) => ({ id: label, label }))
                : (b.amenities || "")
                  .split(",")
                  .map((label) => label.trim())
                  .filter(Boolean)
                  .map((label) => ({ id: label, label })),
            )
            .filter((v) => v && v.label);
          setAmenitiesMaster(fallback);
          return;
        }

        setAmenitiesMaster(normalized);
      } catch {
        setAmenitiesMaster([]);
      }
    };
    fetchAmenities();
  }, [buildingList]);

  const loadProjectsByType = useCallback(
    async (type) => {
      if (!type) {
        lastProjectTypeLoaded.current = null;
        setProjects([]);
        return;
      }
      if (lastProjectTypeLoaded.current === type) return;
      lastProjectTypeLoaded.current = type;
      try {
        setLoadingProjects(true);
        setError('');
        const typedProjects = await getProjects({ projectType: type });
        setProjects(typedProjects || []);
      } catch (err) {
        setError(err.message || 'Failed to load projects');
      } finally {
        setLoadingProjects(false);
      }
    },
    []
  );

  const fetchBuildings = useCallback(async () => {
    try {
      setLoadingBuildings(true);
      setError('');

      const projectFromList = projects.find((p) => p._id === selectedProjectId);
      const typeForFetch =
        projectFromList?.projectType || (draftType && draftType !== "all" ? draftType : "Apartment");
      const response = await getPlotsByProject(
        selectedProjectId || null,
        typeForFetch,
        typeForFetch
      );

      const inventories = Array.isArray(response)
        ? response
        : Array.isArray(response?.inventories)
          ? response.inventories
          : Array.isArray(response?.data?.inventories)
            ? response.data.inventories
            : Array.isArray(response?.data)
              ? response.data
              : [];

      const project = projects.find((p) => p._id === selectedProjectId);

      const formatted = inventories
        .filter((building) => {
          const bType = (building.inventoryType || "Apartment").toLowerCase();
          const targetType = typeForFetch.toLowerCase();
          const typeMatches = bType === targetType || (targetType === 'commercial' && bType === 'apartment');
          // A building should have buildingName or at least not be a flat (which has flatNo)
          const isNotFlat = !building.flatNo && !building.plotNo;
          return typeMatches && isNotFlat;
        })
        .map((building) => {
          const resolvedProject =
            project ||
            projects.find((p) => p._id === (building.projectId || building.projectData?._id || selectedProjectId)) ||
            null;

          const totalFloorsValue = Number.isFinite(Number(building.totalFloors))
            ? Number(building.totalFloors)
            : 0;
          const totalFlatsValue = Number.isFinite(Number(building.totalFlats))
            ? Number(building.totalFlats)
            : 0;

          const bId = building._id || building.id || "";
          const pId = building.projectId || building.projectData?._id || selectedProjectId || "";
          const iType = building.inventoryType || building.projectType || "Apartment";

          return {
            id: bId,
            projectId: pId,
            name: building.buildingName || building.name || "Unnamed Building",
            project: resolvedProject?.projectName || building.projectData?.projectName || building.project || "Unknown",
            projectName: resolvedProject?.projectName || building.projectData?.projectName || building.project || "Unknown",
            projectType: iType,
            type: iType,
            status: building.status || null,
            totalFloors: totalFloorsValue,
            totalFlats: totalFlatsValue,
            startDate: building.startDate || "",
            completionDate: building.completionDate || "",
            amenities: Array.isArray(building.amenities) ? building.amenities : [],
            remark: building.remark || "",
          };
        });

      setBuildingList(formatted);
    } catch (err) {
      setError(err.message || 'Failed to load buildings');
      setBuildingList([]);
    } finally {
      setLoadingBuildings(false);
    }
  }, [selectedProjectId, projects]);


  // Fetch buildings when project changes
  useEffect(() => {
    if (!hasSearched) {
      setBuildingList([]);
      return;
    }
    fetchBuildings();
  }, [selectedProjectId, fetchBuildings, hasSearched]);

  useEffect(() => {
    if (addDialogOpen && newBuilding.projectType) {
      loadProjectsByType(newBuilding.projectType);
    }
  }, [addDialogOpen, newBuilding.projectType, loadProjectsByType]);

  const resetImportState = () => {
    setImportFileName("");
    setSelectedImportFile(null);
    setImportError("");
    setImportSuccess("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleImportClick = () => {
    resetImportState();
    setImportDialogOpen(true);
  };

  const handleCloseImportDialog = () => {
    resetImportState();
    setImportDialogOpen(false);
  };

  const handleChooseImportFile = (event) => {
    const file = event.target.files && event.target.files[0];
    if (file) {
      setSelectedImportFile(file);
      setImportFileName(file.name);
    }
  };

  const handleOpenExportMenu = (event) => {
    setExportMenuAnchorEl(event.currentTarget);
  };

  const handleCloseExportMenu = () => {
    setExportMenuAnchorEl(null);
  };

  const parseNumericValue = (value) => {
    if (value === null || value === undefined || value === "") return "";
    const num = Number(value);
    return Number.isFinite(num) ? num : value;
  };

  const normalizeAmenities = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value.map((item) => (typeof item === "string" ? item.trim() : item)).filter(Boolean);
    }
    if (typeof value === "string") {
      return value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
    return [];
  };

  const handlePerformImport = async () => {
    if (!selectedImportFile) return;
    try {
      const rows = await readExcelFile(selectedImportFile);
      if (!Array.isArray(rows) || rows.length === 0) {
        throw new Error("Excel sheet is empty or invalid.");
      }

      const timestamp = Date.now();
      const normalized = rows
        .map((row, index) => {
          const name =
            row.name || row.Name || row.buildingName || row.BuildingName || row["Building Name"] || "";
          if (!name) {
            return null;
          }
          const project = row.project || row.Project || row["Project Name"] || "";
          const projectType = row.projectType || row.ProjectType || "Apartment";
          const status = (row.status || row.Status || "").toLowerCase();
          const totalFloors = parseNumericValue(row.totalFloors || row.TotalFloors || row["Total Floors"]);
          const totalFlats = parseNumericValue(row.totalFlats || row.TotalFlats || row["Total Flats"]);
          const startDate = row.startDate || row.StartDate || row["Start Date"] || "";
          const completionDate = row.completionDate || row.CompletionDate || row["Completion Date"] || "";
          const amenities = normalizeAmenities(row.amenities || row.Amenities || row["Amenities"]);
          const remark = row.remark || row.Remark || "";

          return {
            id: row.id || row.ID || row.buildingId || row.BuildingId || `IMP-BLD-${timestamp}-${index + 1}`,
            name,
            project,
            projectType,
            status,
            totalFloors: totalFloors === "" ? "" : totalFloors,
            totalFlats: totalFlats === "" ? "" : totalFlats,
            startDate,
            completionDate,
            amenities,
            remark,
          };
        })
        .filter(Boolean);

      if (!normalized.length) {
        throw new Error("No valid building records were found in the file.");
      }

      setBuildingList(normalized);
      setImportSuccess(`Imported ${normalized.length} building(s) successfully.`);
      setImportError("");
      setHasSearched(false);
      setActiveFilters({
        search: "",
        status: "all",
        projectType: "",
        project: "",
      });
      setDraftType("");
      setDraftProject("");
    } catch (error) {
      setImportError(error.message || "Failed to import buildings. Please check the file format.");
      setImportSuccess("");
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDownloadSample = () => {
    try {
      downloadExcelFile({
        rows: [
          {
            buildingId: "BLD-101",
            name: "Sample Tower",
            project: "Sample Project",
            projectType: "Apartment",
            status: "Available",
            totalFloors: 20,
            totalFlats: 160,
            startDate: "2024-01-10",
            completionDate: "2026-06-30",
            amenities: "Gym, Clubhouse, Pool",
            remark: "Sample data row",
          },
        ],
        sheetName: "Sample",
        fileName: "building_import_sample.xlsx",
      });
    } catch (error) {
      setImportError(error.message || "Failed to download sample file.");
    }
  };

  const handleDownloadTemplate = () => {
    try {
      downloadExcelFile({
        rows: [
          {
            buildingId: "",
            name: "",
            project: "",
            projectType: "",
            status: "",
            totalFloors: "",
            totalFlats: "",
            startDate: "",
            completionDate: "",
            amenities: "",
            remark: "",
          },
        ],
        sheetName: "Template",
        fileName: "building_import_template.xlsx",
      });
    } catch (error) {
      setImportError(error.message || "Failed to download template file.");
    }
  };


  const getProjectOptionsForType = useCallback(
    (projectType) => {
      if (!projectType) {
        return (projects || []).map((p) => p.projectName || p.name).filter(Boolean);
      }
      const normalizedType = String(projectType).toLowerCase();
      return (projects || [])
        .filter((p) => String(p.projectType || p.type || 'Apartment').toLowerCase() === normalizedType)
        .map((p) => p.projectName || p.name)
        .filter(Boolean);
    },
    [projects]
  );

  const filterProjectOptions = useMemo(() => {
    if (!draftType || draftType === "all") {
      return ["all", ...Array.from(new Set(buildingList.map((b) => b.project).filter(Boolean)))];
    }
    const options = getProjectOptionsForType(draftType);
    return ["all", ...options];
  }, [buildingList, draftType, getProjectOptionsForType]);

  const addProjectOptions = useMemo(
    () => getProjectOptionsForType(newBuilding.projectType),
    [getProjectOptionsForType, newBuilding.projectType]
  );

  const editProjectOptions = useMemo(
    () => getProjectOptionsForType(editBuilding.projectType),
    [getProjectOptionsForType, editBuilding.projectType]
  );

  const amenityOptions = useMemo(() => {
    if (!Array.isArray(amenitiesMaster)) return [];
    return amenitiesMaster;
  }, [amenitiesMaster]);

  const filteredBuildings = useMemo(() => {
    // Filter buildings based on selected project and search criteria
    return buildingList.filter((building) => {
      const matchesSearch = activeFilters.search
        ? building.name.toLowerCase().includes(activeFilters.search.toLowerCase()) ||
        building.project.toLowerCase().includes(activeFilters.search.toLowerCase()) ||
        building.id.toLowerCase().includes(activeFilters.search.toLowerCase())
        : true;

      const matchesStatus =
        !activeFilters.status ||
        activeFilters.status === "all" ||
        building.status.toLowerCase() === activeFilters.status.toLowerCase();

      const matchesType =
        !activeFilters.projectType ||
        activeFilters.projectType === "all" ||
        building.projectType.toLowerCase() === activeFilters.projectType.toLowerCase();

      const matchesProject =
        !activeFilters.project || activeFilters.project === "all" || building.project === activeFilters.project || building.projectId === activeFilters.project;

      return matchesSearch && matchesStatus && matchesType && matchesProject;
    });
  }, [buildingList, activeFilters]);

  const paginatedBuildings = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredBuildings.slice(start, start + rowsPerPage);
  }, [filteredBuildings, page, rowsPerPage]);

  const canAddBuilding = Boolean(selectedProjectId);
  const isGlobalLoading = loadingBuildings || addSaving || editSaving;

  const buildExportRows = useCallback(() => {
    const source = hasSearched ? filteredBuildings : buildingList;
    return source.map((building) => ({
      Building: building.name,
      "Total Floors": building.totalFloors,
      "Total Flats": building.totalFlats,
      Status: building.status,
      "Start Date": building.startDate,
      "Completion Date": building.completionDate,
      Amenities: Array.isArray(building.amenities) ? building.amenities.join(", ") : "",
      Remark: building.remark,
    }));
  }, [hasSearched, filteredBuildings, buildingList]);

  const handleExportExcel = async () => {
    try {
      const projectFromList = projects.find((p) => p._id === selectedProjectId);
      const typeForFetch =
        projectFromList?.projectType || (draftType && draftType !== "all" ? draftType : "Apartment");

      const response = await getPlotsByProject(
        selectedProjectId || null,
        typeForFetch,
        typeForFetch,
        undefined,
        1,
        100000
      );

      const inventories = Array.isArray(response?.inventories)
        ? response.inventories
        : Array.isArray(response?.data?.inventories)
          ? response.data.inventories
          : Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response)
              ? response
              : [];

      const rows = inventories.map((building) => {
        const resolvedProject =
          projectFromList ||
          projects.find((p) => p._id === (building.projectId || building.projectData?._id)) ||
          null;

        const amenitiesArr = Array.isArray(building.amenities) ? building.amenities : [];
        const totalFloorsValue = Number.isFinite(Number(building.totalFloors))
          ? Number(building.totalFloors)
          : 0;
        const totalFlatsValue = Number.isFinite(Number(building.totalFlats))
          ? Number(building.totalFlats)
          : "";

        return {
          Building: building.buildingName || building.name || "Unnamed Building",
          "Total Floors": totalFloorsValue,
          "Total Flats": totalFlatsValue,
          Status: building.status || "available",
          "Start Date": building.startDate || "",
          "Completion Date": building.completionDate || "",
          Amenities: amenitiesArr.join(", "),
          Remark: building.remark || "",
        };
      });

      if (!rows.length) {
        handleCloseExportMenu();
        return;
      }

      // Prevent download when no meaningful data
      if (!rows.some((r) => r.Building && r.Building !== "Unnamed Building")) {
        handleCloseExportMenu();
        return;
      }

      exportAsExcel({ rows, sheetName: "Buildings", fileNameBase: "buildings" });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Export buildings error", error);
    } finally {
      handleCloseExportMenu();
    }
  };

  const handleExportCSV = () => {
    const rows = buildExportRows();
    if (!rows.length) {
      handleCloseExportMenu();
      return;
    }
    exportAsCSV({ rows, fileNameBase: "buildings" });
    handleCloseExportMenu();
  };

  const handleExportPDF = () => {
    const rows = buildExportRows();
    if (!rows.length) {
      handleCloseExportMenu();
      return;
    }
    const headers = Object.keys(rows[0] || {});
    exportAsPDF({
      rows,
      headers,
      title: "Building List",
      fileNameBase: "buildings",
      headStyles: { fillColor: [25, 118, 210] },
    });
    handleCloseExportMenu();
  };

  const handleViewFloors = (building) => {
    history.push("/app/project/apartment/floors", { building });
  };

  const handleEditBuilding = (building) => {
    loadProjectsByType(building.projectType);
    setEditBuilding({
      id: building.id,
      name: building.name,
      project: building.project,
      projectId: building.projectId || "",
      projectType: building.projectType,
      status: building.status || "available",
      totalFloors: building.totalFloors,
      totalFlats: building.totalFlats,
      floorNo: Number.isFinite(Number(building.floorNo)) ? Number(building.floorNo) : 0,
      startDate: building.startDate,
      completionDate: building.completionDate,
      amenities: Array.isArray(building.amenities) ? building.amenities : [],
      remark: building.remark || "",
    });
    const existingProjects = getProjectOptionsForType(building.projectType);
    setEditProjectCustom(!existingProjects.includes(building.project));
    setEditError("");
    setEditDialogOpen(true);
  };

  const handleDeleteBuilding = async (building) => {
    if (!window.confirm(`Delete ${building.name}? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoadingBuildings(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/projects/inventory/${building.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            isDeleted: true,
          }),
        },
      );
      const body = await response.json().catch(() => ({}));
      const ok = body.status === true || body.success === true || response.ok;
      if (!ok) {
        throw new Error(body.message || "Failed to delete building.");
      }

      await fetchBuildings();
      setHasSearched(true);
      setSuccessMessage(body.message || "Building deleted successfully.");
    } catch (error) {
      setError(error.message || "Failed to delete building.");
    } finally {
      setLoadingBuildings(false);
    }
  };

  const handleOpenAddDialog = () => {
    const typeForProjects = draftType && draftType !== "all" ? draftType : "";
    loadProjectsByType(typeForProjects);
    setNewBuilding({
      name: "",
      project: "",
      projectId: selectedProjectId || "",
      projectType: typeForProjects,
      status: "",
      totalFloors: "",
      startDate: "",
      completionDate: "",
      amenities: [],
      remark: "",
    });
    setAddError("");
    setAddDialogOpen(true);
  };

  const handleNewBuildingNumberChange = (field) => (e) => {
    const raw = e.target.value;
    if (raw === "") {
      setNewBuilding((prev) => ({ ...prev, [field]: "" }));
      return;
    }
    if (String(raw).length > 3) {
      return;
    }
    const num = Number(raw);
    if (Number.isNaN(num) || num < 0) return;
    setNewBuilding((prev) => ({ ...prev, [field]: raw }));
  };

  const handleSaveNewBuilding = async () => {
    const {
      name,
      project,
      projectId,
      projectType,
      status,
      totalFloors,
      totalFlats,
      startDate,
      completionDate,
      amenities,
      remark,
    } = newBuilding;
    if (!status) {
      setAddError("Please select a status.");
      return;
    }
    const statusValue = status.toLowerCase();
    const resolvedProjectId = projectId || selectedProjectId || "";
    setAddError("");

    if (!resolvedProjectId) {
      setAddError("Please select a project.");
      return;
    }

    if (startDate && completionDate && new Date(startDate) > new Date(completionDate)) {
      setAddError("Start Date cannot be later than Completion Date.");
      return;
    }

    const floorsNum = Number(totalFloors);
    const normalizedFloors = Number.isNaN(floorsNum) ? 0 : floorsNum;

    const amenitiesArray = Array.isArray(amenities) ? amenities : [];

    const amenityNames = amenitiesArray
      .map((item) =>
        item && typeof item === "object"
          ? item.details?.amenitiesName || item.label || item.amenitiesName || item.name || item.value
          : item
      )
      .filter(Boolean);

    const payload = {
      projectId: resolvedProjectId,
      inventoryType: projectType || "Apartment",
      buildingName: name || "",
      totalFloors: normalizedFloors,
      totalFlats: Number(totalFlats) || 0,
      startDate: startDate || "",
      completionDate: completionDate || "",
      amenities: amenityNames,
      remark: remark || "",
      status: statusValue,
    };

    try {
      setAddSaving(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/projects/inventory/add`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      const body = await response.json().catch(() => ({}));
      const ok = body.status === true || body.success === true || response.ok;
      if (!ok) {
        const msg = body.message || "Failed to add building.";
        setAddError(msg);
        return;
      }

      setSuccessMessage(body.message || "Building added successfully!");
      setAddDialogOpen(false);
      setHasSearched(true);
      await fetchBuildings();

      const newBuildingId = body.data?._id || body.data?.id;
      if (newBuildingId && normalizedFloors > 0) {
        // Automatically generate floors and flats
        const unitsPerFloor = Number(totalFlats) || 0;
        
        try {
          for (let i = 1; i <= normalizedFloors; i++) {
            // Create the floor
            const floorRes = await createFloor({
              projectId: resolvedProjectId,
              buildingId: newBuildingId,
              floorNo: i,
              totalFlat: unitsPerFloor,
              buildingName: name,
              projectName: selectedProjectName || (project && typeof project === 'object' ? project.projectName || project.name : project),
              status: statusValue || "available",
              remark: ``,
              updatedBy: "Admin",
            });

            const floorId = floorRes.data?._id || floorRes.data?.id || floorRes._id || floorRes.id;
            
            // If floor was created and user specified units, create placeholders for flats
            if (floorId && unitsPerFloor > 0) {
              const flatPromises = [];
              for (let j = 1; j <= unitsPerFloor; j++) {
                const flatNo = i * 100 + j;
                flatPromises.push(
                  createFlat({
                    projectId: resolvedProjectId,
                    buildingId: newBuildingId,
                    floorId: floorId,
                    flatNumber: flatNo,
                    flatType: "2 BHK", // Default placeholder
                    facing: "East",   // Default placeholder
                    status: "available",
                    inventoryType: projectType || "Apartment",
                    remark: ``,
                    price: 0,
                    plcPercent: 0,
                    companyDiscountPercent: 0,
                    cost: 0,
                    guidelineValue: 0
                  })
                );
              }
              // Wait for all flats on this floor to be created before moving to next floor
              await Promise.all(flatPromises);
            }
          }
          setSuccessMessage(body.message + " Floors and flats generated successfully.");
        } catch (err) {
          console.error("[BuildingList] Floor/Flat generation partial failure", err);
          setSuccessMessage(body.message + " However, some floors or flats failed to generate.");
        }
      }
    } catch (error) {
      setAddError(error.message || "Failed to add building.");
    } finally {
      setAddSaving(false);
    }
  };

  const handleUpdateBuilding = async () => {
    const {
      id,
      name,
      project,
      projectId,
      projectType,
      status,
      totalFloors,
      startDate,
      completionDate,
      amenities,
      remark,
    } = editBuilding;
    const projectValue = editProjectCustom ? project.trim() : project;
    if (!id) {
      setEditError("Missing building identifier.");
      return;
    }
    setEditError("");

    if (startDate && completionDate && new Date(startDate) > new Date(completionDate)) {
      setEditError("Start Date cannot be later than Completion Date.");
      return;
    }

    const floorsNum = Number(totalFloors);
    const normalizedFloors = Number.isNaN(floorsNum) ? 0 : floorsNum;

    const amenitiesArray = Array.isArray(amenities) ? amenities : [];

    try {
      setEditSaving(true);
      const amenityNames = amenitiesArray
        .map((item) =>
          item && typeof item === "object"
            ? item.details?.amenitiesName || item.label || item.amenitiesName || item.name || item.value
            : item
        )
        .filter(Boolean);

      const payload = {
        projectId: projectId || selectedProjectId || "",
        inventoryType: projectType || "Apartment",
        projectType,
        buildingName: name,
        totalFloors: normalizedFloors,
        startDate,
        completionDate,
        amenities: amenityNames,
        remark,
        status: (status || "available").toLowerCase(),
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/projects/inventory/${id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      const body = await response.json().catch(() => ({}));
      const ok = body.status === true || body.success === true || response.ok;
      if (!ok) {
        throw new Error(body.message || "Failed to save building changes.");
      }

      setBuildingList((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
              ...item,
              name,
              project: projectValue,
              projectId: projectId || selectedProjectId || "",
              projectType,
              status,
              totalFloors: normalizedFloors,
              startDate,
              completionDate,
              amenities: amenitiesArray,
              remark,
            }
            : item
        )
      );

      setSuccessMessage(body.message || "Building updated successfully.");
      setEditDialogOpen(false);
      setHasSearched(true);
      await fetchBuildings();
    } catch (error) {
      setEditError(error.message || "Failed to save building changes.");
    } finally {
      setEditSaving(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return "—";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleViewFlats = (building) => {
    history.push({
      pathname: "/app/project/apartment-commercial/flat-management",
      state: {
        type: building.projectType || building.type || "",
        project: building.projectId || "",
        building: building.id || building._id || "",
      },
    });
  };

  const statusChipStyle = (status) => {
    const s = (status || "").toLowerCase();
    switch (s) {
      case "available":
        return { backgroundColor: "rgba(76, 175, 80, 0.12)", color: "#2e7d32", textTransform: "capitalize" };
      case "underconstruction":
        return { backgroundColor: "rgba(255, 193, 7, 0.12)", color: "#f57c00", textTransform: "capitalize" };
      default:
        return { backgroundColor: "rgba(158, 158, 158, 0.12)", color: "#616161", textTransform: "capitalize" };
    }
  };

  if (!canRead) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh', p: 3 }}>
        <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, textAlign: 'center', maxWidth: 500, borderRadius: 3, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
          <AvatarMui sx={{ bgcolor: '#fee2e2', color: '#ef4444', width: 80, height: 80, mb: 3, mx: 'auto' }}>
            <WarningIcon sx={{ fontSize: 50 }} />
          </AvatarMui>
          <Typography variant="h4" fontWeight="bold" color="#1a202c" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
            You do not have permission to view Building List. Please contact your administrator for access.
          </Typography>
          <Button
            variant="contained"
            onClick={() => history.push('/app/dashboard')}
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3d5afe' },
              px: 4, py: 1.2, borderRadius: 2, fontWeight: 600, textTransform: 'none'
            }}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1, flexDirection: "column" }}
        open={isGlobalLoading}
      >
        <CircularProgress color="inherit" />
        <Typography variant="body2" sx={{ mt: 1.5 }}>
          Please wait...
        </Typography>
      </Backdrop>
      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, justifyContent: "space-between", alignItems: { xs: "flex-start", md: "center" }, mb: 3, gap: 2 }}>
        <Box sx={{ mb: { xs: 2, md: 0 } }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#000" }}>
            Building List
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage buildings within your apartment projects
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1.2, alignItems: "center", flexWrap: "wrap", width: { xs: "100%", md: "auto" } }}>
          {canWrite && (
            <>
              <Button
                variant="contained"
                startIcon={<FileUploadIcon />}
                color="primary"
                onClick={handleImportClick}
                sx={{ textTransform: "none", fontWeight: 600, flex: { xs: 1, sm: "none" } }}
              >
                Import
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                color="primary"
                onClick={handleOpenExportMenu}
                sx={{ textTransform: "none", fontWeight: 600, flex: { xs: 1, sm: "none" } }}
              >
                Export
              </Button>
              <Menu
                anchorEl={exportMenuAnchorEl}
                open={Boolean(exportMenuAnchorEl)}
                onClose={handleCloseExportMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <MenuItem onClick={handleExportExcel}>Export as Excel</MenuItem>
                <MenuItem onClick={handleExportCSV}>Export as CSV</MenuItem>
                <MenuItem onClick={handleExportPDF}>Export as PDF</MenuItem>
              </Menu>
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenAddDialog}
                sx={{ textTransform: "none", fontWeight: 600, width: { xs: "100%", sm: "auto" } }}
                disabled={!canAddBuilding}
                title={!canAddBuilding ? "Select a project first" : undefined}
              >
                Add Building
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Card sx={{ mb: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.08)", borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                select
                fullWidth
                label="Property Type"
                value={draftType}
                onChange={(e) => {
                  const value = e.target.value;
                  setDraftType(value);
                  setDraftProject("");
                  setSelectedProjectId("");
                  setSelectedProjectName("");
                  setHasSearched(false);
                  setBuildingList([]);
                  setActiveFilters({
                    search: "",
                    status: "all",
                    projectType: value ? value : "",
                    project: "",
                  });
                  loadProjectsByType(value ? value : "");
                }}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
              >
                <option value="">Select Type</option>
                <option value="Apartment">Apartment</option>
                <option value="Commercial">Commercial</option>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                select
                fullWidth
                label="Project"
                value={selectedProjectId}
                onChange={(e) => {
                  const projectId = e.target.value;
                  const project = projects.find(p => p._id === projectId);
                  const projectType = project?.projectType || (draftType && draftType !== "all" ? draftType : "");
                  setSelectedProjectId(projectId);
                  setSelectedProjectName(project ? project.projectName : '');

                  if (projectId) {
                    setSearchError("");
                    setHasSearched(true);
                    setActiveFilters({
                      search: "",
                      status: "all",
                      projectType,
                      project: projectId || "",
                    });
                    setPage(0);
                  } else {
                    setHasSearched(false);
                    setActiveFilters({
                      search: "",
                      status: "all",
                      projectType: "",
                      project: "",
                    });
                    setDraftType("");
                    setDraftProject("");
                  }
                }}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                disabled={loadingProjects}
              >
                <option value="">Select a Project</option>
                {projects.map((project) => (
                  <option key={project._id} value={project._id}>
                    {project.projectName}
                  </option>
                ))}
              </TextField>
            </Grid>
          </Grid>
          {searchError && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="error">
                {searchError}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card sx={{ boxShadow: "0 2px 12px rgba(0,0,0,0.1)", borderRadius: 2 }}>
        <CardContent>
          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: "none" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Building</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    Total Floors
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    Total Flats
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Start Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Completion Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Amenities</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Remark</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingBuildings ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                      <CircularProgress />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Loading buildings...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : paginatedBuildings.map((building) => (
                  <TableRow hover key={building.id}>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {building.name}
                      </Typography>
                      {/* <Typography variant="caption" color="text.secondary" display="block">
                        {building.id}
                      </Typography> */}
                      {/* <Typography variant="caption" color="text.secondary" display="block">
                        {building.project} · {building.projectType}
                      </Typography> */}
                    </TableCell>
                    <TableCell align="center">{building.totalFloors}</TableCell>
                    <TableCell align="center">{building.totalFlats}</TableCell>
                    <TableCell>
                      <Chip label={building?.status || "select status"} size="small" sx={statusChipStyle(building?.status || "select status")} />
                    </TableCell>
                    <TableCell>{formatDate(building.startDate)}</TableCell>
                    <TableCell>{formatDate(building.completionDate)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {Array.isArray(building.amenities) && building.amenities.length
                          ? building.amenities.join(", ")
                          : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {building.remark || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ px: 2 }}>
                      <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={0.75}>
                        <Tooltip title="View Floors">
                          <IconButton size="small" color="primary" onClick={() => handleViewFloors(building)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {/* <Tooltip title="View Flats">
                          <IconButton size="small" color="secondary" onClick={() => handleViewFlats(building)}>
                            <ApartmentIcon fontSize="small" />
                          </IconButton>
                        </Tooltip> */}
                        {canWrite ? (
                          <>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleEditBuilding(building)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" color="error" onClick={() => handleDeleteBuilding(building)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <></>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}

                {paginatedBuildings.length === 0 && !loadingBuildings && (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        {hasSearched
                          ? "No buildings found for the selected project."
                          : "Select a project to view buildings."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            // rowsPerPageOptions={[5, 10, 25]}
            count={filteredBuildings.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </CardContent>
      </Card>

      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Building</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Property Type"
                value={newBuilding.projectType}
                onChange={(e) => {
                  const value = e.target.value;
                  setNewBuilding((prev) => ({ ...prev, projectType: value, project: "", projectId: "" }));
                  loadProjectsByType(value);
                }}
              >
                <MenuItem value="">
                  <em>Select Type</em>
                </MenuItem>
                <MenuItem value="Apartment">Apartment</MenuItem>
                <MenuItem value="Commercial">Commercial</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Project"
                value={newBuilding.project}
                onChange={(e) => {
                  const value = e.target.value;
                  const selected = projects.find((p) => p.projectName === value);
                  setNewBuilding((prev) => ({
                    ...prev,
                    project: value,
                    projectId: selected?._id || "",
                  }));
                }}
                fullWidth
                disabled={!newBuilding.projectType}
              >
                <MenuItem value="">
                  <em>{newBuilding.projectType ? "Select Project" : "Select type first"}</em>
                </MenuItem>
                {addProjectOptions.map((project) => (
                  <MenuItem key={project} value={project}>
                    {project}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Building Name"
                value={newBuilding.name}
                onChange={(e) => setNewBuilding((prev) => ({ ...prev, name: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Status"
                value={newBuilding.status}
                onChange={(e) => setNewBuilding((prev) => ({ ...prev, status: e.target.value }))}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
              >
                <option value="">Please Select</option>
                <option value="available">Available</option>
                <option value="underConstruction">Under Construction</option>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Total Floors"
                type="number"
                value={newBuilding.totalFloors}
                onChange={handleNewBuildingNumberChange("totalFloors")}
                fullWidth
                inputProps={{ min: 0, max: 999, inputMode: "numeric", pattern: "[0-9]*" }}
                onWheel={(e) => e.target.blur()}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Units per Floor"
                type="number"
                value={newBuilding.totalFlats}
                onChange={handleNewBuildingNumberChange("totalFlats")}
                fullWidth
                inputProps={{ min: 0, max: 999, inputMode: "numeric", pattern: "[0-9]*" }}
                onWheel={(e) => e.target.blur()}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Start Date"
                type="date"
                value={newBuilding.startDate}
                onChange={(e) => setNewBuilding((prev) => ({ ...prev, startDate: e.target.value }))}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Completion Date"
                type="date"
                value={newBuilding.completionDate}
                onChange={(e) => setNewBuilding((prev) => ({ ...prev, completionDate: e.target.value }))}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={amenityOptions}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(opt, val) => (opt?.id || opt) === (val?.id || val)}
                value={newBuilding.amenities}
                onChange={(_, value) =>
                  setNewBuilding((prev) => ({
                    ...prev,
                    amenities: value,
                  }))
                }
                filterSelectedOptions
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      size="small"
                      label={option?.label || option}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Amenities" placeholder="Select amenities" fullWidth />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Remark"
                value={newBuilding.remark}
                onChange={(e) => setNewBuilding((prev) => ({ ...prev, remark: e.target.value }))}
                fullWidth
                multiline
                rows={2}
                placeholder="Any additional notes"
              />
            </Grid>
          </Grid>
          {addError && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {addError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveNewBuilding} disabled={addSaving}>
            {addSaving ? "Saving..." : "Save Building"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Building</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Building Name"
                value={editBuilding.name}
                onChange={(e) => setEditBuilding((prev) => ({ ...prev, name: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Property Type"
                value={editBuilding.projectType}
                disabled
              >
                <MenuItem value="">
                  <em>Select Type</em>
                </MenuItem>
                <MenuItem value="Apartment">Apartment</MenuItem>
                <MenuItem value="Commercial">Commercial</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Project"
                value={editBuilding.project}
                fullWidth
                disabled

              >
                <MenuItem value="">
                  <em>{editBuilding.projectType ? "Select Project" : "Select type first"}</em>
                </MenuItem>
                {editProjectOptions.map((project) => (
                  <MenuItem key={project} value={project}>
                    {project}
                  </MenuItem>
                ))}
                <MenuItem value="__custom__">Add New Project</MenuItem>
              </TextField>
            </Grid>
            {editProjectCustom && (
              <Grid item xs={12}>
                <TextField
                  label="New Project Name"
                  value={editBuilding.project}
                  onChange={(e) => setEditBuilding((prev) => ({ ...prev, project: e.target.value }))}
                  fullWidth
                  hidden
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Status"
                value={editBuilding.status}
                onChange={(e) => setEditBuilding((prev) => ({ ...prev, status: e.target.value }))}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
              >
                <option value="">Please Select</option>
                <option value="available">Available</option>
                <option value="underConstruction">Under Construction</option>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Total Floors"
                type="number"
                value={editBuilding.totalFloors}
                onChange={(e) => setEditBuilding((prev) => ({ ...prev, totalFloors: e.target.value }))}
                fullWidth

              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Start Date"
                type="date"
                value={editBuilding.startDate}
                onChange={(e) => setEditBuilding((prev) => ({ ...prev, startDate: e.target.value }))}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Completion Date"
                type="date"
                value={editBuilding.completionDate}
                onChange={(e) => setEditBuilding((prev) => ({ ...prev, completionDate: e.target.value }))}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={amenityOptions}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(opt, val) => (opt?.id || opt) === (val?.id || val)}
                value={editBuilding.amenities}
                onChange={(_, value) => setEditBuilding((prev) => ({ ...prev, amenities: value }))}
                filterSelectedOptions
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      size="small"
                      label={option?.label || option}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Amenities" placeholder="Select amenities" fullWidth />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Remark"
                value={editBuilding.remark}
                onChange={(e) => setEditBuilding((prev) => ({ ...prev, remark: e.target.value }))}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
          {editError && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {editError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateBuilding} disabled={editSaving}>
            {editSaving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={importDialogOpen} onClose={handleCloseImportDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Import Buildings</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <DialogContentText>
              Upload an Excel file (.xlsx or .xls) with columns such as <code>name</code>,{" "}
              <code>project</code>, <code>projectType</code>, <code>status</code>, <code>totalFloors</code>,
              <code>totalFlats</code>, <code>startDate</code>, and <code>completionDate</code>.
            </DialogContentText>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Button
                variant="text"
                startIcon={<FileDownloadIcon />}
                sx={{ textTransform: "none" }}
                onClick={handleDownloadSample}
              >
                Download Sample
              </Button>
              <Button
                variant="text"
                startIcon={<FileDownloadIcon />}
                sx={{ textTransform: "none" }}
                onClick={handleDownloadTemplate}
              >
                Download Template
              </Button>
            </Stack>
            {importError && (
              <Alert severity="error" onClose={() => setImportError("")}>
                {importError}
              </Alert>
            )}
            {importSuccess && (
              <Alert severity="success" onClose={() => setImportSuccess("")}>
                {importSuccess}
              </Alert>
            )}
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                variant="outlined"
                sx={{ textTransform: "none" }}
                onClick={() => fileInputRef.current?.click()}
              >
                Choose File
              </Button>
              {importFileName && (
                <Typography variant="caption" color="text.secondary">
                  {importFileName}
                </Typography>
              )}
            </Stack>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              hidden
              onChange={handleChooseImportFile}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handlePerformImport}
            disabled={!selectedImportFile}
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BuildingList;
